import { cloneDeep } from 'es-toolkit';

import { logError } from '@zg-rentals/log-error';

import api from '../utils/api';
import ListingConversion from '../model/ListingConversion';
import Properties from '../model/Properties';
import AddressRestrictions from '../model/AddressRestrictions';

import DispatchConstants from '../constants/DispatchConstants';
import PropertyConstants from '../constants/PropertyConstants';

import getLocationRestrictionErrorMessage from '../utils/getLocationRestrictionErrorMessageUtils';
import numberUtils from '../utils/numberUtils';

import ListingInt from '../model/ListingsInt';
import RentGuaranteeDisclaimers from '../model/RentGuarantee/RentGuaranteeDisclaimers';
import WebAnalytics from '../model/WebAnalytics';

const PropertiesActions = {
  getListingAliasForListingId({ listingId, originalUrl }) {
    return (dispatch) =>
      dispatch(api.users.properties.getListingAliasForListingId({ listingId, originalUrl }))
        .then((response = {}) => {
          if (response.success === false) {
            return Promise.reject(response.error);
          }
          return response;
        })
        .catch((err) => Promise.reject(err));
  },
  createManualVersion(listingAlias) {
    return (dispatch) =>
      dispatch(api.users.properties.feedListingManagement.createManualVersion(listingAlias))
        .then((response = {}) => {
          if (response.success === false) {
            return Promise.reject(response.error);
          }
          return Promise.resolve(response.response);
        })
        .catch((err) => Promise.reject(err));
  },
  isApplicationsAccepted(propertyId, isApplicationsAccepted = true, requireUserName = false) {
    return (dispatch) =>
      dispatch(
        api.users.properties.isApplicationsAccepted(propertyId, isApplicationsAccepted || false, requireUserName),
      )
        .then((response = {}) => {
          if (response.success === false) {
            return Promise.reject(response.error);
          }
          return response;
        })
        .catch((err) => Promise.reject(err));
  },
  // get interstitial (intent) listings
  getIntListings(type, params) {
    return (dispatch) =>
      dispatch(api.users.properties.listingInt(type, params))
        .then((response) => {
          const { error: errorRMWModel, success } = response;

          if (response && !success && errorRMWModel) {
            return Promise.reject(errorRMWModel);
          }

          if (success && response.response) {
            return ListingInt(response.response);
          }

          return response;
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.getIntListings',
          });

          return Promise.reject(errorRMWModel);
        });
  },
  updateAccordionState(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_ACCORDION_STATE,
        payload,
      });
  },
  updateCurrentProperty(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY,
        // meta: { throttle: 500 },
        payload,
      });
  },
  /**
   * @param {boolean} payload - new value for displayTotalMonthlyPriceWithFee
   */
  updateCurrentPropertyDisplayTotalMonthlyPriceWithFee(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY_DISPLAY_TOTAL_MONTHLY_PRICE_WITH_FEE,
        payload,
      });
  },
  updateCurrentPropertyBuildingAttributes(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY_BUILDING_ATTRIBUTES,
        payload,
      });
  },
  updateCurrentPropertyCurrentRoommatesDetails(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY_CURRENT_ROOMMATES_DETAILS,
        payload,
      });
  },
  updateCurrentPropertyCurrentPets(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY_CURRENT_PETS,
        payload,
      });
  },
  updateCurrentPropertyRoommateRequirements(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY_ROOMMATE_REQUIREMENTS,
        payload,
      });
  },
  updateCurrentPropertyRentalProtection(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY_RENTAL_PROTECTION,
        payload,
      });
  },
  updateCurrentPropertyRentersInsurance(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY_RENTERS_INSURANCE,
        payload,
      });
  },
  updateCurrentPropertyRestrictions(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_RESTRICTIONS,
        payload,
      });
  },
  updateCurrentPropertyDateEmailVerified(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_DATE_EMAIL_VERIFIED,
        payload,
      });
  },
  updateCurrentPropertyFreemium(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY_FREEMIUM,
        payload,
      });
  },
  getCurrentPropertyAddressRestrictions(currentProperty) {
    const {
      street,
      zip,
      city,
      unit,
      state,
      listingTypeCode,
      propertyTypeCode,
      listingAlias,
      hasActiveMultifamilyContract,
    } = currentProperty;
    const address = `${street}, ${city}, ${state} ${zip}`;
    return (dispatch, getState) => {
      dispatch({
        type: DispatchConstants.UPDATE_CURRENT_PROPERTY,
        payload: {
          addressRestrictions: AddressRestrictions(),
        },
      });
      return dispatch(
        api.properties.locations.getRestrictions({
          address,
          unit,
          listingTypeCode,
          listingAlias,
          propertyTypeCode,
        }),
      )
        .then((result) => {
          if (result.success && result.response) {
            const { user } = getState();
            const isMultifamily =
              PropertyConstants.PROPERTY_TYPES.COMMUNITY === PropertyConstants.PROPERTY_TYPES_BY_CODE[propertyTypeCode];
            const errorMessage = getLocationRestrictionErrorMessage(
              result.response,
              isMultifamily,
              user.isMultifamilyCustomer,
              hasActiveMultifamilyContract,
              user.isSingleFamilyEnterpriseCustomer,
              user.isPostingPathAdmin,
              user.isHousingConnector,
            );
            if (errorMessage) {
              return dispatch({
                type: DispatchConstants.UPDATE_CURRENT_PROPERTY,
                payload: {
                  addressRestrictions: AddressRestrictions({
                    hasRestrictionErrorMessage: true,
                    errorMessage,
                    paidListingExists: result.response.paidListingExists,
                  }),
                },
              });
            }
            return Promise.resolve();
          }

          return Promise.reject(result.error);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.getCurrentPropertyAddressRestrictions',
          });
          return Promise.reject(errorRMWModel);
        });
    };
  },
  updatePropertyType({ propertyId, propertyTypeCode }) {
    const propertyTypeData = {
      fieldName: 'propertyTypeCode',
      propertyTypeCode,
      propertyId,
    };

    return (dispatch) =>
      dispatch(api.users.properties.edit(propertyTypeData))
        .then((response) => {
          const { error: errorRMWModel, success } = response;

          if (!success) {
            return Promise.reject(errorRMWModel);
          }

          return response;
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.updatePropertyType',
          });

          return Promise.reject(errorRMWModel);
        });
  },
  savePropertyInfo({ fullAddress = '', unit = undefined, numUnits = '', propertyId = '', propertyTypeCode = '' } = {}) {
    const propertyType = PropertyConstants.PROPERTY_TYPES_BY_CODE[propertyTypeCode];

    return (dispatch) => {
      const addressData = {
        fullAddress,
        propertyId,
        unitNumber: unit,
        propertyTypeCode,
      };
      // TODO: we really need to switch to the multi endpoint for this
      return dispatch(api.properties.address.updateAddress(addressData))
        .then((response) => {
          let { error: errorRMWModel } = response;

          if (response && !response.success) {
            if (!errorRMWModel) {
              errorRMWModel = {
                data: '',
                code: '',
                message: 'Something went wrong - Error is undefined',
              };
            }
            logError({
              error: errorRMWModel,
              context: 'PropertiesActions.savePropertyInfo',
            });

            return Promise.reject(errorRMWModel);
          }

          const { street, city, state, zip, lat, lon } = response.response.addressDetails;

          if (propertyType === PropertyConstants.PROPERTY_TYPES.COMMUNITY && numUnits !== '') {
            return dispatch(
              api.users.properties.edit({
                fieldName: 'unitsInBuilding',
                unitsInBuilding: numUnits,
                propertyId,
              }),
            );
          }
          dispatch(
            PropertiesActions.updateCurrentProperty({
              propertyType,
              street,
              city,
              state,
              zip,
              unit,
              geo: {
                lat,
                lon,
              },
              numUnits,
            }),
          );
        })
        .catch((errorRMWModel) => Promise.reject(errorRMWModel));
    };
  },
  updateFilters(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_PROPERTIES_FILTERS,
        payload,
      });
  },
  updateFiltersTouched(hasSubNavFiltersBeenTouched) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_SUB_NAV_FILTERS_TOUCHED,
        payload: hasSubNavFiltersBeenTouched,
      });
  },
  updateProperties(listingsModel) {
    const propertiesModel = Properties(listingsModel);
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_PROPERTIES,
        payload: {
          listingsModel,
        },
      });
  },
  getListingConversionStatus() {
    return (dispatch) =>
      dispatch(api.users.properties.feedToManual.statuses())
        .then((response = {}) => {
          const { error: errorRMWModel, success } = response;

          if (response && !success && errorRMWModel) {
            return Promise.reject(errorRMWModel);
          }

          const listingConversion = ListingConversion(response);

          dispatch({
            type: DispatchConstants.UPDATE_PROPERTIES_CONVERSION,
            payload: listingConversion,
          });
          return Promise.resolve(listingConversion);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.getListingConversionStatus',
          });
          return Promise.reject(errorRMWModel.message);
        });
  },
  getDisclaimers(listingAlias, disclaimerTypes) {
    return (dispatch) =>
      dispatch(api.properties.rentGuarantee.getDisclaimers(listingAlias, disclaimerTypes))
        .then((result) => {
          const { error: errorRMWModel, success, response } = result;

          if (!success) {
            return Promise.reject(errorRMWModel);
          }
          const rentGuaranteeDisclaimers = RentGuaranteeDisclaimers(response);

          return Promise.resolve(rentGuaranteeDisclaimers);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.getDisclaimers',
          });

          return Promise.reject(errorRMWModel);
        });
  },
  selectRentalProtectionCoverageLevelForListing({ listingAlias, premiumCoverageId }) {
    return (dispatch) =>
      dispatch(api.properties.rentGuarantee.storeListingPremiumCoverageSelection({ listingAlias, premiumCoverageId }))
        .then((result) => {
          const { error: errorRMWModel, success, response } = result;
          if (!success) {
            return Promise.reject(errorRMWModel);
          }
          return Promise.resolve(response);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.selectRentalProtectionCoverageLevelForListing',
          });
          return Promise.reject(errorRMWModel.message);
        });
  },
  updatePremiumCoverageSelectionForPolicy({ listingAlias, policyId, premiumCoverageId }) {
    return (dispatch) =>
      dispatch(
        api.properties.rentGuarantee.updatePremiumCoverageSelectionForPolicy({
          listingAlias,
          policyId,
          premiumCoverageId,
        }),
      )
        .then((result) => {
          const { error: errorRMWModel, success, response } = result;
          if (!success) {
            return Promise.reject(errorRMWModel);
          }
          return Promise.resolve(response);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.updatePremiumCoverageSelectionForPolicy',
          });
          return Promise.reject(errorRMWModel.message);
        });
  },
  storeEffectivePolicyScreenDisplayed({ listingAlias = '', userToken = '' }) {
    return (dispatch) =>
      dispatch(api.properties.rentGuarantee.storeEffectivePolicyScreenDisplayed({ listingAlias, userToken }))
        .then((result) => {
          const { error: errorRMWModel, success, response } = result;

          if (!success) {
            return Promise.reject(errorRMWModel);
          }
          return Promise.resolve(response);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.storeEffectivePolicyScreenDisplayed',
          });

          return Promise.reject(errorRMWModel);
        });
  },
  reinstatePolicy(dataToApi) {
    return (dispatch) =>
      dispatch(api.properties.rentGuarantee.reinstatePolicy(dataToApi))
        .then((result) => {
          const { error: errorRMWModel, success, response } = result;

          if (!success) {
            return Promise.reject(errorRMWModel);
          }

          // sync current property
          dispatch(
            PropertiesActions.updateCurrentPropertyRentalProtection({
              isEnabled: true,
            }),
          );

          return Promise.resolve(response);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.reinstatePolicy',
          });

          return Promise.reject(errorRMWModel);
        });
  },
  getSecureClaimsUrlForPolicy(params) {
    return (dispatch) =>
      dispatch(api.properties.rentGuarantee.getSecureClaimsUrlForPolicy(params))
        .then((result) => {
          const { error: errorRMWModel, success, response } = result;

          if (!success) {
            return Promise.reject(errorRMWModel);
          }
          return Promise.resolve(response);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.cancelRentalProtectionPolicy',
          });

          return Promise.reject(errorRMWModel);
        });
  },
  updateRentPriceForListing(rentPrice) {
    return (dispatch, getState) => {
      const {
        properties: {
          currentProperty: { floorplans },
        },
      } = getState();

      if (floorplans.length > 0) {
        const copyFloorplan = cloneDeep(floorplans);
        const value = Number(numberUtils.asCurrency(rentPrice, '0.00'));
        copyFloorplan[0].price = value;

        dispatch(
          PropertiesActions.updateCurrentProperty({
            price: value,
            floorplans: copyFloorplan,
          }),
        );
      }
    };
  },
  updateSecurityDepositForListing(propertyId, securityDeposit) {
    return (dispatch, getState) =>
      dispatch(api.properties.rentGuarantee.updateSecurityDepositForListing(propertyId, securityDeposit))
        .then((result) => {
          const { error: errorRMWModel, success, response } = result;
          const { isSecurityDepositWaived } = response || {};

          if (!success) {
            return Promise.reject(errorRMWModel);
          }

          // sync with currentProperty
          const {
            properties: {
              currentProperty: { floorplans },
            },
          } = getState();

          // floorplans from rental-protection page might not exist
          if (floorplans.length > 0) {
            const copyFloorplan = cloneDeep(floorplans);
            const value = Number(numberUtils.asCurrency(securityDeposit, '0.00'));
            copyFloorplan[0].securityDeposit = value;

            dispatch(
              PropertiesActions.updateCurrentProperty({
                securityDeposit: value,
                floorplans: copyFloorplan,
              }),
            );
          }

          dispatch(PropertiesActions.updateCurrentPropertyRentalProtection({ isSecurityDepositWaived }));

          return Promise.resolve(response);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.updateSecurityDepositForListing',
          });

          return Promise.reject(errorRMWModel);
        });
  },
  updateZrmToolsAndSecurityDepositReviewed(listingAlias) {
    return (dispatch) =>
      dispatch(api.properties.rentGuarantee.updateZrmToolsAndSecurityDepositReviewed(listingAlias))
        .then((result) => {
          const { error: errorRMWModel, success, response } = result;

          if (!success) {
            return Promise.reject(errorRMWModel);
          }
          return Promise.resolve(response);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.updateZrmToolsAndSecurityDepositReviewed',
          });

          return Promise.reject(errorRMWModel);
        });
  },
  updateOrWaiveSecurityDepositForListing(
    listingAlias,
    securityDeposit,
    waiveSecurityDeposit,
    reviewedListingSecurityDepositForLightweight,
  ) {
    return (dispatch, getState) =>
      dispatch(
        api.properties.rentGuarantee.updateOrWaiveSecurityDepositForListing(
          listingAlias,
          securityDeposit,
          waiveSecurityDeposit,
          reviewedListingSecurityDepositForLightweight,
        ),
      )
        .then((result) => {
          const { error: errorRMWModel, success, response } = result;
          const { isSecurityDepositWaived } = response || {};

          if (!success) {
            return Promise.reject(errorRMWModel);
          }

          // sync with currentProperty
          const {
            properties: {
              currentProperty: { floorplans },
            },
          } = getState();

          // floorplans from rental-protection page might not exist
          if (floorplans.length > 0) {
            const copyFloorplan = cloneDeep(floorplans);
            const value = Number(numberUtils.asCurrency(securityDeposit, '0.00'));
            copyFloorplan[0].securityDeposit = value;

            dispatch(
              PropertiesActions.updateCurrentProperty({
                securityDeposit: value,
                floorplans: copyFloorplan,
              }),
            );
          }

          dispatch(PropertiesActions.updateCurrentPropertyRentalProtection({ isSecurityDepositWaived }));

          return Promise.resolve(response);
        })
        .catch((errorRMWModel) => {
          logError({
            error: errorRMWModel,
            context: 'PropertiesActions.updateSecurityDepositForListing',
          });

          return Promise.reject(errorRMWModel);
        });
  },
  updateWebAnalytics: (webAnalytics) => (dispatch, _) =>
    dispatch({
      type: DispatchConstants.UPDATE_WEB_ANALYTICS,
      payload: WebAnalytics(webAnalytics),
    }),
  updatePostActivationState(payload) {
    return (dispatch) =>
      dispatch({
        type: DispatchConstants.UPDATE_POST_ACTIVATION_STATE,
        payload,
      });
  },
};

export default PropertiesActions;
